import React from "react";
import { scoreBand, TONE_CLASSES } from "./constants";

/**
 * ScoreRing — one 0-100 score as a ring with the number in the middle.
 *
 * ⚠️ Hand-rolled SVG rather than a recharts radial, and the reason is the
 * palette rule rather than taste. recharts sets `fill` as an SVG PRESENTATION
 * ATTRIBUTE, where `var()` does not resolve, so a chart has to read its colours
 * through `useTokenColors` — one colour for the life of the component. This
 * ring changes colour with the value (a 42 must not be painted the same green as
 * an 88), which would mean either a literal per band or a token read per render.
 *
 * `stroke="currentColor"` sidesteps both: `currentColor` DOES resolve in a
 * presentation attribute, so the band's Tailwind text utility on the wrapper
 * paints the arc, and the token underneath re-themes it for free in dark mode
 * and under per-institute branding.
 */
const ScoreRing = ({
  value,
  size = 132,
  thickness = 10,
  label = "",
  sublabel = "",
  showBand = true,
}) => {
  const has = value !== null && value !== undefined && Number.isFinite(Number(value));
  const pct = has ? Math.max(0, Math.min(100, Number(value))) : 0;
  const band = scoreBand(has ? pct : null);
  const tone = TONE_CLASSES[band.tone];

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (pct / 100) * circumference;

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
      <div className={`tw-relative tw-shrink-0 ${tone.ring}`} style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
          aria-label={has ? `Score ${Math.round(pct)} out of 100` : "Not yet scored"}>
          {/* Track. A neutral at low alpha rather than a surface token: the ring
              sits on cards of two different backgrounds in this module. */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke="currentColor" strokeWidth={thickness}
            className="tw-text-content-subtle" opacity="0.16"
          />
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke="currentColor" strokeWidth={thickness}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
            // -90° so the arc starts at twelve o'clock instead of three.
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            className="tw-transition-[stroke-dasharray] tw-duration-700 tw-ease-out motion-reduce:tw-transition-none"
          />
        </svg>
        <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center">
          <span className={`tw-font-semibold tw-leading-none ${tone.text}`}
            style={{ fontSize: size * 0.28 }}>
            {has ? Math.round(pct) : "—"}
          </span>
          {label && (
            <span className="tw-mt-1 tw-text-[0.7rem] tw-font-medium tw-uppercase tw-tracking-caps tw-text-content-subtle">
              {label}
            </span>
          )}
        </div>
      </div>
      {showBand && (
        <div className="tw-text-center">
          <div className={`tw-text-sm tw-font-semibold ${tone.text}`}>{band.label}</div>
          {sublabel && <div className="tw-text-xs tw-text-content-subtle">{sublabel}</div>}
        </div>
      )}
    </div>
  );
};

/**
 * The same score as a horizontal bar — for the six section rows, where a grid of
 * rings would be six competing focal points and no comparison.
 */
export const ScoreBar = ({ value, label, weight, verdict }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = TONE_CLASSES[scoreBand(pct).tone];
  return (
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3">
        <span className="tw-text-sm tw-font-medium tw-text-content">{label}</span>
        <span className="tw-flex tw-items-baseline tw-gap-1.5">
          {weight > 0 && (
            <span className="tw-text-[0.7rem] tw-text-content-subtle">{weight}% weight</span>
          )}
          <span className={`tw-text-sm tw-font-semibold tw-tabular-nums ${tone.text}`}>{pct}</span>
        </span>
      </div>
      <div className="tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-surface-sunken">
        <div
          className={`tw-h-full tw-rounded-full tw-bg-current tw-transition-[width] tw-duration-500 motion-reduce:tw-transition-none ${tone.text}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {verdict && (
        <p className="tw-m-0 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">{verdict}</p>
      )}
    </div>
  );
};

export default ScoreRing;
